/**
 * Drop Behavior
 *
 * Handles drag-and-drop of files onto the canvas.
 * Registers native DOM dragover/dragenter/dragleave/drop listeners
 * on the container element. Updates ViewState.dropTarget for visual feedback.
 *
 * Accepts all files — the app decides how to handle each type via onFileDrop.
 */

import type { DropBehaviorContext } from "./types";
import * as viewState from "../view-state";

/**
 * Check if a DataTransfer contains any files.
 */
function hasFiles(dataTransfer: DataTransfer): boolean {
  // Check items (preferred — available during dragover)
  if (dataTransfer.items) {
    for (let i = 0; i < dataTransfer.items.length; i++) {
      if (dataTransfer.items[i].kind === "file") {
        return true;
      }
    }
  }

  // Fallback to files (may not be populated during dragover in all browsers)
  if (dataTransfer.files && dataTransfer.files.length > 0) {
    return true;
  }

  return false;
}

/**
 * Extract all files from a DataTransfer.
 */
function getFiles(dataTransfer: DataTransfer): File[] {
  const files: File[] = [];
  for (let i = 0; i < dataTransfer.files.length; i++) {
    files.push(dataTransfer.files[i]);
  }
  return files;
}

/**
 * Register drop behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerDropBehavior(ctx: DropBehaviorContext): () => void {
  const {
    container,
    getViewState,
    updateViewState,
    screenToWorld,
    onFileDrop,
    log,
  } = ctx;

  // Track drag enter/leave depth to handle nested elements
  let dragDepth = 0;

  function handleDragEnter(e: DragEvent): void {
    if (!e.dataTransfer) return;
    if (!hasFiles(e.dataTransfer)) return;

    e.preventDefault();
    dragDepth++;

    if (dragDepth === 1) {
      log?.("drop-enter", "Files dragged over canvas");
      updateViewState(viewState.showDropTarget(getViewState()));
    }
  }

  function handleDragOver(e: DragEvent): void {
    if (!e.dataTransfer) return;
    if (!hasFiles(e.dataTransfer)) return;

    // Must preventDefault to indicate we accept the drop
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }

  function handleDragLeave(e: DragEvent): void {
    if (!e.dataTransfer) return;

    dragDepth--;

    if (dragDepth <= 0) {
      dragDepth = 0;
      log?.("drop-leave", "Files dragged away from canvas");
      updateViewState(viewState.hideDropTarget(getViewState()));
    }
  }

  function handleDrop(e: DragEvent): void {
    e.preventDefault();
    dragDepth = 0;

    // Always hide drop target on drop
    updateViewState(viewState.hideDropTarget(getViewState()));

    if (!e.dataTransfer) return;

    const files = getFiles(e.dataTransfer);
    if (files.length === 0) return;

    // Convert drop screen position to world coordinates
    const rect = container.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const worldPos = screenToWorld(screenX, screenY);

    log?.(
      "drop",
      `${files.length} file(s) dropped at (${worldPos.x.toFixed(0)}, ${worldPos.y.toFixed(0)})`,
    );

    onFileDrop({ files, position: worldPos });
  }

  // Register DOM listeners
  container.addEventListener("dragenter", handleDragEnter);
  container.addEventListener("dragover", handleDragOver);
  container.addEventListener("dragleave", handleDragLeave);
  container.addEventListener("drop", handleDrop);

  return () => {
    container.removeEventListener("dragenter", handleDragEnter);
    container.removeEventListener("dragover", handleDragOver);
    container.removeEventListener("dragleave", handleDragLeave);
    container.removeEventListener("drop", handleDrop);

    // Clean up drop target state if active
    if (getViewState().dropTarget) {
      updateViewState(viewState.hideDropTarget(getViewState()));
    }
  };
}
